import { createContext, createElement, useContext } from 'react';

import type { LayoutAwareVideoHost } from '@src/components/sidepanel/useLayoutAwareVideoSettingsState';
import type { TextBrandingService } from '@src/components/textProcessor/TextBrandingService';
import type { TextProducer } from '@src/components/textProcessor/TextProducer';
import type { CaptionsToggleHost } from '@src/components/transcript/hooks/useCaptionsToggle';
import type { ElementState } from '@src/renderer/ElementState';
import type KaraokeProducer from '@src/services/karaoke/KaraokeProducer';
import type { SceneLayoutIndexStore } from '@src/stores-v2/SceneLayoutIndexStore';
import type { BrandingCustomFont } from '@src/types.ts/story';
import type { AspectRatio } from '@src/types.ts/video';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { ReactNode } from 'react';

/** The settings panels a bar's More can open. */
export type ToolbarSettingsPanel = 'text' | 'photo' | 'video' | 'captions';

/**
 * PLAT-12101 (PLAT-12097 DE9) — everything the floating toolbars read, handed
 * down by whoever mounts them, instead of `useVideoCreatorStore()`.
 *
 * That store is provided app-wide, so inside the Content Studio clip preview
 * it is the Creator Studio store, not the clip's: a bar reading it there would
 * quietly edit the wrong video. Creator Studio builds this from
 * `VideoCreatorStore` (`stage/creatorStudioToolbarHost.ts`); the clip preview
 * builds its own (PLAT-12102). Read members on every render — the Creator
 * Studio host is getters over MobX state, so observers track them as before.
 */
export interface FloatingToolbarHost {
  // Selection and playback
  readonly activeElementIds: readonly string[];
  getActiveElement(): ElementState | null | undefined;
  /** Selects an element on the canvas, as a click on it would. */
  selectElement(id: string): void;
  readonly isPlaying: boolean;

  /** The renderer's canvas, which the bars position against. */
  readonly canvasElement: HTMLElement | null | undefined;

  // The source
  /** Top-level elements of the source (only `id` is read; see `isBarTarget`). */
  readonly topLevelElements: ReadonlyArray<{ id?: unknown }> | undefined;
  isOriginalVideoElement(source: ElementState['source']): boolean;

  // Edits
  readonly editorStore: EditorStoreLike;
  /** Saves right after a Delete, as the graphics-card chip does (PLAT-12098 DS3). */
  persist(): void;

  /** The text bar's config mirror and unit helpers (PLAT-12088). */
  readonly textProducer: Pick<
    TextProducer,
    | 'config'
    | 'getTextConfig'
    | 'setTextConfig'
    | 'convertToPx'
    | 'convertToVh'
    | 'modifyProperty'
  >;

  /** What the video bar's layout-aware settings read. */
  readonly layout: LayoutAwareVideoHost;

  /** What the captions bar reads beside the caption store. */
  readonly captions: {
    readonly textBrandingService: TextBrandingService;
    readonly karaokeProducer: Pick<
      KaraokeProducer,
      'getKaraokeTextSettingByAspectRatio'
    >;
    readonly time: number;
    readonly aspectRatio: AspectRatio;
    readonly customFonts: BrandingCustomFont[];
    readonly sceneLayoutIndexStore: SceneLayoutIndexStore | undefined;
    /** The original video's element under the playhead (scene-aware position). */
    readonly originalVideoElementIdAtTime: string | undefined;
    readonly toggle: CaptionsToggleHost;
    /**
     * Whether the captions bar offers Style. Off where another control on the
     * surface already picks the caption style (the clip preview's player-bar
     * Captions picker, PLAT-12102 DS4).
     */
    readonly offerStyle: boolean;
  };

  /**
   * Render the bars in a body-level layer at this z-index instead of in place
   * (PLAT-12102 DS2). The clip preview needs it: its canvas iframe sits above
   * the modal in a fixed layer, and the modal's stage is transformed and
   * clipped, so an in-place bar would be mispositioned and painted under it.
   * Creator Studio sets none: in place, inside the stage (PLAT-11264).
   */
  readonly layer?: { readonly zIndex: number };

  /**
   * Opens and closes a settings panel. Absent where the surface has none (the
   * clip preview): every bar then leaves More out (DE5).
   */
  readonly settings?: {
    isOpen(panel: ToolbarSettingsPanel): boolean;
    toggle(panel: ToolbarSettingsPanel): void;
  };

  /**
   * Opens the selected text's content editor, anchored to the text bar's Edit
   * (PLAT-12102). The clip preview routes it to the canvas layer's quick-edit
   * popover, the one its chip's pencil opened before the bar replaced the
   * chip. Absent where the panel edits the content (Creator Studio): the text
   * bar then has no Edit.
   */
  readonly editText?: (anchor: HTMLElement) => void;
}

const FloatingToolbarHostContext = createContext<FloatingToolbarHost | null>(
  null,
);

export const FloatingToolbarHostProvider = ({
  host,
  children,
}: {
  host: FloatingToolbarHost;
  children: ReactNode;
}) =>
  createElement(FloatingToolbarHostContext.Provider, { value: host }, children);

/** The mounting surface's host; `null` outside a provider — bars render nothing. */
export function useFloatingToolbarHost(): FloatingToolbarHost | null {
  return useContext(FloatingToolbarHostContext);
}
